import { Button, Card, Typography } from "@norican/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { DocumentTitle } from "@/components/document-title";
import { I2qLogo } from "@/components/i2q-logo";
import { Microsoft } from "@/components/icons/microsoft";
import { NoricanLogo } from "@/components/norican-logo";
import { logger } from "@/lib/logger";
import { PRODUCT_SHORT, PRODUCT_SPELLED_OUT } from "@/lib/product";

// Local dev can't complete a real Microsoft sign-in (Entra won't redirect back
// to localhost), so the dev server shows a login picker that hits the
// backend's `/auth/dev-login` instead — a genuine session, just without
// Microsoft. Production builds always show the Microsoft button; the backend
// refuses `/auth/dev-login` there regardless (see DEV_AUTH_BYPASS).
const SHOW_DEV_LOGIN = import.meta.env.DEV;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState("");
  const [devSigningIn, setDevSigningIn] = useState(false);

  const handleMicrosoftLogin = () => {
    logger.debug("[login] redirecting to Microsoft SSO");
    window.location.assign("/api/auth/sso/login");
  };

  const handleDevLogin = async (role: "user" | "admin") => {
    logger.debug("[login] dev sign-in as", role);
    setError("");
    setDevSigningIn(true);
    try {
      const response = await fetch(`/api/auth/dev-login?role=${role}`, { method: "POST" });
      if (!response.ok) {
        throw new Error(`dev-login responded ${response.status}`);
      }
      logger.debug("[login] dev sign-in succeeded → redirecting to /queue");
      navigate("/queue");
    } catch (err) {
      logger.debug("[login] dev sign-in failed:", err);
      setError("Local sign-in failed. Please try again.");
      setDevSigningIn(false);
    }
  };

  useEffect(() => {
    const ssoError = searchParams.get("error");
    if (!ssoError) {
      return;
    }
    logger.debug("[login] sso error:", ssoError);
    setError(
      ssoError === "domain_not_allowed"
        ? "Your account cannot sign in to this app."
        : "Sign-in failed. Try again.",
    );
  }, [searchParams]);

  return (
    <div className="bg-bg flex min-h-screen items-center justify-center px-4">
      <DocumentTitle title="Sign in" />
      <Card className="w-full max-w-md">
        <Card.Header className="flex flex-col items-center text-center">
          {/* The 2026 primary logo (with tagline): the login is where people first meet
              the brand (guide p.13). 54 px box = 35 px of artwork, the digital minimum
              (p.17); the clear space (p.16) is built into the file. Blue on light, white on
              dark. */}
          <NoricanLogo kind="primary" height={54} className="mb-1" />
          {/* font-display font-normal leading-display: Turbine Regular at 96 % line height
              for titles (guide p.23), ds-gap G-5. */}
          {/* The product mark beside the product name: the mark is decorative here, the
              heading names the product. */}
          <div className="mt-2 flex items-center gap-3">
            <I2qLogo tone="tile" size={40} decorative />
            <Typography
              as="h1"
              variant="heading-2"
              className="font-display leading-display font-normal"
            >
              {PRODUCT_SHORT}
            </Typography>
          </div>
          <p className="text-fg-muted text-sm">
            {PRODUCT_SPELLED_OUT}: customer inquiries to quotations
          </p>
          <p className="text-fg-muted text-sm">Sign in to continue</p>
          <p className="text-fg-subtle mt-1 text-xs">Shadow mode: read-only, nothing is sent</p>
        </Card.Header>
        <Card.Body>
          {SHOW_DEV_LOGIN ? (
            <div className="space-y-2">
              <p className="text-fg-muted text-center text-xs">
                Local dev. Microsoft sign-in only works on the deployed app.
              </p>
              <Button
                type="button"
                disabled={devSigningIn}
                onClick={() => handleDevLogin("user")}
                fullWidth
              >
                Continue as user
              </Button>
              <Button
                type="button"
                disabled={devSigningIn}
                variant="outline"
                onClick={() => handleDevLogin("admin")}
                fullWidth
              >
                Continue as admin
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              onClick={handleMicrosoftLogin}
              startIcon={<Microsoft className="size-4" />}
              fullWidth
            >
              Sign in with Microsoft
            </Button>
          )}
          {error && <p className="text-danger-fg mt-4 text-center text-sm">{error}</p>}
        </Card.Body>
      </Card>
    </div>
  );
}
